import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Row, Col, Tabs, Tab } from "react-bootstrap";
import {
  MovingAverage,
  LogicCondition,
  RiskParameters,
  MACrossoverParams,
  MAType,
  ShutdownDay,
  RestartDay,
  ScheduleParameters,
  Algo,
  AssetClass,
  Symbol,
} from "../../types";
import { Trash2, Plus } from "lucide-react";

interface AddAlgoModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (algo: Partial<Algo>) => void;
  assetClasses: AssetClass[];
  symbols: Symbol[];
  initialData?: Algo;
  isEditing?: boolean;
}

const AddAlgoModal: React.FC<AddAlgoModalProps> = ({
  show,
  onHide,
  onAdd,
  assetClasses,
  symbols,
  initialData,
  isEditing = false,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [selectedAssetClasses, setSelectedAssetClasses] = useState<number[]>(
    initialData?.asset_classes?.map((ac) => ac.id) || []
  );
  const [selectedSymbols, setSelectedSymbols] = useState<number[]>(
    initialData?.symbols?.map((s) => s.id) || []
  );

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description || "");
      setSelectedAssetClasses(
        initialData.asset_classes?.map((ac) => ac.id) || []
      );
      setSelectedSymbols(initialData.symbols?.map((s) => s.id) || []);
    }
  }, [initialData]);

  // Moving Averages State
  const [movingAverages, setMovingAverages] = useState<MovingAverage[]>([
    {
      type: MAType.SMA,
      period: 21,
      priceType: "close",
      shift: 0,
    },
    {
      type: MAType.SMA,
      period: 50,
      priceType: "close",
      shift: 0,
    },
  ]);

  // Entry/Exit Conditions State
  const [buyConditions, setBuyConditions] = useState<LogicCondition[]>([
    {
      indicator1: movingAverages[0],
      relationship: "Crosses above",
      indicator2: movingAverages[1],
    },
  ]);

  const [sellConditions, setSellConditions] = useState<LogicCondition[]>([
    {
      indicator1: movingAverages[0],
      relationship: "Crosses below",
      indicator2: movingAverages[1],
    },
  ]);

  // Exit Conditions State
  const [buyExitConditions, setBuyExitConditions] = useState<LogicCondition[]>(
    []
  );
  const [sellExitConditions, setSellExitConditions] = useState<
    LogicCondition[]
  >([]);

  // Risk Parameters State
  const [riskParams, setRiskParams] = useState<RiskParameters>({
    lotSizePerSymbol: "0.1",
    lotMode: "Fixed",
    lotValue: 0.1,
    riskSource: "Balance",
    riskPercentage: 2.0,
    maxDrawdownPercentage: 5.0,
    addSpreadToSL: true,
    addSpreadToTP: true,
    tpPerSymbol: "100",
    slPerSymbol: "50",
    maxOrderCount: 5,
    breakevenAfterProfit: 100,
    closeTradeAfterDrawdown: 50,
    useTrailingStop: false,
    trailingStopPoints: 20,
    useTrailingTakeProfit: false,
    trailingTakeProfitPoints: 20,
    useShutdown: false,
    closeDay: ShutdownDay.FRIDAY,
    closeTime: "17:00",
    restartDay: RestartDay.MONDAY,
    restartTime: "09:30",
    dailyStartTime: "09:30",
    dailyStopTime: "16:00",
    useIntrabarCalculation: false,
    executionReversal: false,
    allowPyramiding: false,
    maxPyramidPositions: 3,
    pyramidSize: 0.5,
    orderType: "Market",
    maxRiskPerSymbol: "",
    maxRiskPerSymbolType: "Fixed",
  });

  // Schedule Parameters State
  const [scheduleParams, setScheduleParams] = useState<ScheduleParameters>({
    activeShutdown: false,
    closeDay: "",
    closeTime: "",
    restartDay: "",
    restartTime: "",
    dailyStartTime: "09:30",
    dailyStopTime: "16:00",
  });

  const renderConditionRow = (
    condition: LogicCondition,
    index: number,
    conditions: LogicCondition[],
    setConditions: React.Dispatch<React.SetStateAction<LogicCondition[]>>
  ) => (
    <Row key={index} className="mb-3 align-items-center">
      <Col>
        <Form.Select
          value={`${condition.indicator1.type}_${condition.indicator1.period}`}
          onChange={(e) => {
            const newConditions = [...conditions];
            const [type, period] = e.target.value.split("_");
            const selectedMA = movingAverages.find(
              (ma) => ma.type === type && ma.period === parseInt(period)
            );
            if (selectedMA) {
              newConditions[index] = {
                ...condition,
                indicator1: selectedMA,
              };
              setConditions(newConditions);
            }
          }}
        >
          {movingAverages.map((ma) => (
            <option
              key={`${ma.type}_${ma.period}`}
              value={`${ma.type}_${ma.period}`}
            >
              {ma.type} {ma.period}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col>
        <Form.Select
          value={condition.relationship}
          onChange={(e) => {
            const newConditions = [...conditions];
            newConditions[index] = {
              ...condition,
              relationship: e.target.value as
                | "Crosses above"
                | "Crosses below"
                | "Is above"
                | "Is below",
            };
            setConditions(newConditions);
          }}
        >
          <option value="Crosses above">Crosses above</option>
          <option value="Crosses below">Crosses below</option>
          <option value="Is above">Is above</option>
          <option value="Is below">Is below</option>
        </Form.Select>
      </Col>
      <Col>
        <Form.Select
          value={`${condition.indicator2.type}_${condition.indicator2.period}`}
          onChange={(e) => {
            const newConditions = [...conditions];
            const [type, period] = e.target.value.split("_");
            const selectedMA = movingAverages.find(
              (ma) => ma.type === type && ma.period === parseInt(period)
            );
            if (selectedMA) {
              newConditions[index] = {
                ...condition,
                indicator2: selectedMA,
              };
              setConditions(newConditions);
            }
          }}
        >
          {movingAverages.map((ma) => (
            <option
              key={`${ma.type}_${ma.period}`}
              value={`${ma.type}_${ma.period}`}
            >
              {ma.type} {ma.period}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs="auto">
        <Button
          variant="danger"
          onClick={() =>
            setConditions(conditions.filter((_, i) => i !== index))
          }
        >
          <Trash2 size={16} />
        </Button>
      </Col>
    </Row>
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      name,
      description,
      asset_classes: selectedAssetClasses.map((id) => ({ id })),
      symbols: selectedSymbols.map((id) => ({ id })),
      parameters: {
        strategy_type: "MA_CROSSOVER",
        movingAverages,
        entryConditions: {
          buyConditions,
          sellConditions,
        },
        exitConditions: {
          buyExitConditions,
          sellExitConditions,
        },
        riskParameters: riskParams,
        scheduleParameters: scheduleParams,
      },
    });
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? "Edit Algorithm" : "Add Algorithm"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Strategy Name</Form.Label>
          <Form.Control
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter strategy name"
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter strategy description"
          />
        </Form.Group>

        <Tabs defaultActiveKey="indicators">
          <Tab eventKey="indicators" title="Indicators">
            <div className="p-3">
              <h5>Moving Averages</h5>
              {movingAverages.map((ma, index) => (
                <Row key={index} className="mb-3 align-items-center">
                  <Col>
                    <Form.Select
                      value={ma.type}
                      onChange={(e) => {
                        const newMAs = [...movingAverages];
                        newMAs[index].type = e.target.value as MAType;
                        setMovingAverages(newMAs);
                      }}
                    >
                      {Object.values(MAType).map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col>
                    <Form.Control
                      type="number"
                      value={ma.period}
                      onChange={(e) => {
                        const newMAs = [...movingAverages];
                        newMAs[index].period = parseInt(e.target.value);
                        setMovingAverages(newMAs);
                      }}
                    />
                  </Col>
                  <Col>
                    <Form.Select
                      value={ma.priceType}
                      onChange={(e) => {
                        const newMAs = [...movingAverages];
                        newMAs[index].priceType = e.target.value as any;
                        setMovingAverages(newMAs);
                      }}
                    >
                      <option value="close">Close</option>
                      <option value="open">Open</option>
                      <option value="high">High</option>
                      <option value="low">Low</option>
                      <option value="hl2">HL/2</option>
                      <option value="hlc3">HLC/3</option>
                      <option value="ohlc4">OHLC/4</option>
                    </Form.Select>
                  </Col>
                  <Col xs="auto">
                    <Button
                      variant="danger"
                      onClick={() => {
                        setMovingAverages(
                          movingAverages.filter((_, i) => i !== index)
                        );
                      }}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button
                variant="primary"
                onClick={() => {
                  setMovingAverages([
                    ...movingAverages,
                    {
                      type: MAType.SMA,
                      period: 21,
                      priceType: "close",
                      shift: 0,
                    },
                  ]);
                }}
              >
                <Plus size={16} className="me-2" />
                Add Moving Average
              </Button>
            </div>
          </Tab>

          <Tab eventKey="entry-logic" title="Entry Logic">
            <div className="p-3">
              <h5>Buy Conditions</h5>
              {buyConditions.map((condition, index) =>
                renderConditionRow(
                  condition,
                  index,
                  buyConditions,
                  setBuyConditions
                )
              )}
              <Button
                variant="primary"
                onClick={() => {
                  if (movingAverages.length >= 2) {
                    setBuyConditions([
                      ...buyConditions,
                      {
                        indicator1: movingAverages[0],
                        relationship: "Crosses above",
                        indicator2: movingAverages[1],
                      },
                    ]);
                  }
                }}
              >
                <Plus size={16} className="me-2" />
                Add Buy Condition
              </Button>

              <h5 className="mt-4">Sell Conditions</h5>
              {sellConditions.map((condition, index) =>
                renderConditionRow(
                  condition,
                  index,
                  sellConditions,
                  setSellConditions
                )
              )}
              <Button
                variant="primary"
                onClick={() => {
                  if (movingAverages.length >= 2) {
                    setSellConditions([
                      ...sellConditions,
                      {
                        indicator1: movingAverages[0],
                        relationship: "Crosses below",
                        indicator2: movingAverages[1],
                      },
                    ]);
                  }
                }}
              >
                <Plus size={16} className="me-2" />
                Add Sell Condition
              </Button>
            </div>
          </Tab>

          <Tab eventKey="exit-logic" title="Exit Logic">
            <div className="p-3">
              <h5>Buy Exit Conditions</h5>
              {buyExitConditions.map((condition, index) =>
                renderConditionRow(
                  condition,
                  index,
                  buyExitConditions,
                  setBuyExitConditions
                )
              )}
              <Button
                variant="primary"
                className="mb-4"
                onClick={() => {
                  setBuyExitConditions([
                    ...buyExitConditions,
                    {
                      indicator1: movingAverages[0],
                      relationship: "Crosses below",
                      indicator2: movingAverages[1],
                    },
                  ]);
                }}
              >
                <Plus size={16} className="me-2" />
                Add Buy Exit Condition
              </Button>

              <h5>Sell Exit Conditions</h5>
              {sellExitConditions.map((condition, index) =>
                renderConditionRow(
                  condition,
                  index,
                  sellExitConditions,
                  setSellExitConditions
                )
              )}
              <Button
                variant="primary"
                onClick={() => {
                  setSellExitConditions([
                    ...sellExitConditions,
                    {
                      indicator1: movingAverages[0],
                      relationship: "Crosses above",
                      indicator2: movingAverages[1],
                    },
                  ]);
                }}
              >
                <Plus size={16} className="me-2" />
                Add Sell Exit Condition
              </Button>
            </div>
          </Tab>

          <Tab eventKey="risk-management" title="Risk Management">
            <div className="p-3">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Lot Size Per Symbol</Form.Label>
                    <Form.Control
                      value={riskParams.lotSizePerSymbol}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          lotSizePerSymbol: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Risk Source</Form.Label>
                    <Form.Select
                      value={riskParams.riskSource}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          riskSource: e.target.value as
                            | "FreeMargin"
                            | "Balance"
                            | "Equity",
                        })
                      }
                    >
                      <option value="FreeMargin">Free Margin</option>
                      <option value="Balance">Balance</option>
                      <option value="Equity">Equity</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Risk Percentage</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={riskParams.riskPercentage}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          riskPercentage: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Max Drawdown %</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.1"
                      value={riskParams.maxDrawdownPercentage}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          maxDrawdownPercentage: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="addSpreadToSL"
                    label="Add Spread to SL"
                    checked={riskParams.addSpreadToSL}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        addSpreadToSL: e.target.checked,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="addSpreadToTP"
                    label="Add Spread to TP"
                    checked={riskParams.addSpreadToTP}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        addSpreadToTP: e.target.checked,
                      })
                    }
                  />
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>TP per Symbol</Form.Label>
                    <Form.Control
                      type="text"
                      value={riskParams.tpPerSymbol}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          tpPerSymbol: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>SL per Symbol</Form.Label>
                    <Form.Control
                      type="text"
                      value={riskParams.slPerSymbol}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          slPerSymbol: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Max Risk Per Symbol Type</Form.Label>
                    <Form.Select
                      value={riskParams.maxRiskPerSymbolType}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          maxRiskPerSymbolType: e.target.value as
                            | "Fixed"
                            | "Percentage",
                        })
                      }
                    >
                      <option value="Fixed">Fixed ($)</option>
                      <option value="Percentage">Percentage (%)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>
                      Max Risk Per Symbol (
                      {riskParams.maxRiskPerSymbolType === "Fixed" ? "$" : "%"})
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="EURUSD=1000,GBPUSD=500"
                      value={riskParams.maxRiskPerSymbol}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          maxRiskPerSymbol: e.target.value,
                        })
                      }
                    />
                    <Form.Text className="text-muted">
                      Format: SYMBOL=VALUE, SYMBOL=VALUE (e.g.,
                      EURUSD=1000,GBPUSD=500)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Tab>

          <Tab eventKey="execution" title="Execution">
            <div className="p-3">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Order Type</Form.Label>
                    <Form.Select
                      value={riskParams.orderType}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          orderType: e.target.value as
                            | "Market"
                            | "Limit"
                            | "Stop"
                            | "Stop Limit",
                        })
                      }
                    >
                      <option value="Market">Market</option>
                      <option value="Limit">Limit</option>
                      <option value="Stop">Stop</option>
                      <option value="Stop Limit">Stop Limit</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Max Order Count</Form.Label>
                    <Form.Control
                      type="number"
                      value={riskParams.maxOrderCount}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          maxOrderCount: parseInt(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="allowPyramiding"
                    label="Allow Pyramiding"
                    checked={riskParams.allowPyramiding}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        allowPyramiding: e.target.checked,
                      })
                    }
                  />
                  {riskParams.allowPyramiding && (
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Pyramid Amount</Form.Label>
                        <Form.Control
                          type="number"
                          value={riskParams.maxPyramidPositions}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              maxPyramidPositions: parseInt(e.target.value),
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Col>
              </Row>
            </div>
          </Tab>

          <Tab eventKey="time-management" title="Time">
            <div className="p-3">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Daily Start Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={riskParams.dailyStartTime}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          dailyStartTime: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Daily Stop Time</Form.Label>
                    <Form.Control
                      type="time"
                      value={riskParams.dailyStopTime}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          dailyStopTime: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Check
                type="switch"
                id="useShutdown"
                label="Use Weekly Shutdown"
                checked={riskParams.useShutdown}
                onChange={(e) =>
                  setRiskParams({
                    ...riskParams,
                    useShutdown: e.target.checked,
                  })
                }
                className="mb-3"
              />

              {riskParams.useShutdown && (
                <>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Close Day</Form.Label>
                        <Form.Select
                          value={riskParams.closeDay}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              closeDay: e.target.value as ShutdownDay,
                            })
                          }
                        >
                          {Object.values(ShutdownDay).map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Close Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={riskParams.closeTime}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              closeTime: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Restart Day</Form.Label>
                        <Form.Select
                          value={riskParams.restartDay}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              restartDay: e.target.value as RestartDay,
                            })
                          }
                        >
                          {Object.values(RestartDay).map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Restart Time</Form.Label>
                        <Form.Control
                          type="time"
                          value={riskParams.restartTime}
                          onChange={(e) =>
                            setRiskParams({
                              ...riskParams,
                              restartTime: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </>
              )}
            </div>
          </Tab>

          <Tab eventKey="schedule" title="Schedule">
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="activeShutdown"
                label="Enable Schedule"
                checked={scheduleParams.activeShutdown}
                onChange={(e) =>
                  setScheduleParams({
                    ...scheduleParams,
                    activeShutdown: e.target.checked,
                  })
                }
              />
            </Form.Group>

            {scheduleParams.activeShutdown && (
              <>
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Daily Start Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleParams.dailyStartTime}
                        onChange={(e) =>
                          setScheduleParams({
                            ...scheduleParams,
                            dailyStartTime: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Daily Stop Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleParams.dailyStopTime}
                        onChange={(e) =>
                          setScheduleParams({
                            ...scheduleParams,
                            dailyStopTime: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Weekly Close Day</Form.Label>
                      <Form.Select
                        value={scheduleParams.closeDay}
                        onChange={(e) =>
                          setScheduleParams({
                            ...scheduleParams,
                            closeDay: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Day</option>
                        {[
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                        ].map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Close Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleParams.closeTime}
                        onChange={(e) =>
                          setScheduleParams({
                            ...scheduleParams,
                            closeTime: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Weekly Restart Day</Form.Label>
                      <Form.Select
                        value={scheduleParams.restartDay}
                        onChange={(e) =>
                          setScheduleParams({
                            ...scheduleParams,
                            restartDay: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Day</option>
                        {[
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                        ].map((day) => (
                          <option key={day} value={day}>
                            {day}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Restart Time</Form.Label>
                      <Form.Control
                        type="time"
                        value={scheduleParams.restartTime}
                        onChange={(e) =>
                          setScheduleParams({
                            ...scheduleParams,
                            restartTime: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Tab>

          <Tab eventKey="trade-management" title="Trade Management">
            <div className="p-3">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Breakeven After Profit</Form.Label>
                    <Form.Control
                      type="number"
                      value={riskParams.breakevenAfterProfit}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          breakevenAfterProfit: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Close Trade After Drawdown</Form.Label>
                    <Form.Control
                      type="number"
                      value={riskParams.closeTradeAfterDrawdown}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          closeTradeAfterDrawdown: parseFloat(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="useTrailingStop"
                    label="Use Trailing Stop"
                    checked={riskParams.useTrailingStop}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        useTrailingStop: e.target.checked,
                      })
                    }
                  />
                  {riskParams.useTrailingStop && (
                    <Form.Control
                      type="number"
                      value={riskParams.trailingStopPoints}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          trailingStopPoints: parseInt(e.target.value),
                        })
                      }
                    />
                  )}
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="useTrailingTakeProfit"
                    label="Use Trailing Take Profit"
                    checked={riskParams.useTrailingTakeProfit}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        useTrailingTakeProfit: e.target.checked,
                      })
                    }
                  />
                  {riskParams.useTrailingTakeProfit && (
                    <Form.Control
                      type="number"
                      value={riskParams.trailingTakeProfitPoints}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          trailingTakeProfitPoints: parseInt(e.target.value),
                        })
                      }
                    />
                  )}
                </Col>
              </Row>
            </div>
          </Tab>

          <Tab eventKey="order-settings" title="Order Settings">
            <div className="p-3">
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Order Type</Form.Label>
                    <Form.Select
                      value={riskParams.orderType}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          orderType: e.target.value as
                            | "Market"
                            | "Limit"
                            | "Stop"
                            | "Stop Limit",
                        })
                      }
                    >
                      <option value="Market">Market</option>
                      <option value="Limit">Limit</option>
                      <option value="Stop">Stop</option>
                      <option value="Stop Limit">Stop Limit</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Max Order Count</Form.Label>
                    <Form.Control
                      type="number"
                      value={riskParams.maxOrderCount}
                      onChange={(e) =>
                        setRiskParams({
                          ...riskParams,
                          maxOrderCount: parseInt(e.target.value),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="executionReversal"
                    label="Execution Reversal"
                    checked={riskParams.executionReversal}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        executionReversal: e.target.checked,
                      })
                    }
                  />
                </Col>
                <Col md={6}>
                  <Form.Check
                    type="switch"
                    id="allowPyramiding"
                    label="Allow Pyramiding"
                    checked={riskParams.allowPyramiding}
                    onChange={(e) =>
                      setRiskParams({
                        ...riskParams,
                        allowPyramiding: e.target.checked,
                      })
                    }
                  />
                </Col>
              </Row>

              {riskParams.allowPyramiding && (
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Pyramid Amount</Form.Label>
                      <Form.Control
                        type="number"
                        value={riskParams.maxPyramidPositions}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            maxPyramidPositions: parseInt(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Pyramid Size</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.1"
                        value={riskParams.pyramidSize}
                        onChange={(e) =>
                          setRiskParams({
                            ...riskParams,
                            pyramidSize: parseFloat(e.target.value),
                          })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              )}
            </div>
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {isEditing ? "Save Changes" : "Add Algorithm"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddAlgoModal;
